import moment from "moment";
import { getLocalStorage } from "utils/localStorage";
import Toolbar from "react-big-calendar/lib/Toolbar";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { centreOptions } from "constants";
import { buildDateTime } from "utils/dateHelper";

export const initialFormState = {
  studentIds: [],
  lesson: "",
  course: undefined,
  date: moment(new Date()).format("yyyy-MM-DD"),
  startTime: moment(new Date()).format("HH:mm"),
  endTime: moment(new Date()).format("HH:mm"),
  centre: centreOptions[0].value,
  remark: "",
  location: "",
  teacher: {},
  homeworkTime: undefined,
  isTrialClass: undefined,
  isTest: false,
  isRecurring: false,
  isUrgentClass: false,
  recurrEndDate: moment().format("yyyy-MM-DD"),
  observerIds: [],
};

export const customLessonInitialFormState = {
  name: "",
};

export const initialModalState = {
  show: false,
  event: null,
};

export const initialDropdownState = {
  show: false,
  x: 0,
  y: 0,
};

export const colorMap = {
  HH: { name: "orange", code: "#ff8d72" },
  CP: { name: "azure", code: "#1d8cf8" },
  RB: { name: "red", code: "#f5365c" },
  RBS: { name: "red", code: "#f5365c" },
  TRB: { name: "green", code: "#2dce89" },
  TP: { name: "blue", code: "#5e72e4" },
  KLT: { name: "purple", code: "#8965e0" },
  RH: { name: "pink", code: "#f3a4b5" },
  BH: { name: "yellow", code: "#ffd600" },
  ZH: { name: "cyan", code: "#2bffc6" },
  Home: { name: "light-blue", code: "#11cdef" },
  SFR: { name: "light-blue", code: "#11cdef" },
  Online: { name: "black", code: "#000000" },
  Birthday: { name: "birthday", code: "#FF8D01" },
};

export const actions = {
  VIEW_DAY: 0,
  NEW_EVENT: 1,
  ADD_ATTENDANCE_REPORT: 2,
};

const getBirthday = (birthday) => {
  if (!birthday) return null;
  let date = moment(birthday);
  const today = moment();
  date = date.year(today.year());
  return date;
};

export const transformData = {
  lessonRecord: (events) =>
    events.map((event) => {
      return {
        ...event,
        title: `${event?.location ? `[${event.location}] ` : ""}${event.isCancelled ? "CXL" : ""} (${
          event.teacher?.fullName || "Unassigned"
        }) - ${event?.students?.map((student) => student.studentDetail?.firstName + " " + student.studentDetail?.lastName)} - ${
          event?.course?.name
        } ${event?.homeworkTime ? "(HW)" : ""}`,
        start: buildDateTime(event.date, event.startTime),
        end: buildDateTime(event.date, event.endTime),
        allDay: false,
        color: event.isCancelled ? "#FF0000" : colorMap[event.centre]?.name,
        isLessonRecord: true,
      };
    }),
  leaves: (leaves) =>
    leaves.map((leave) => {
      const date = moment(leave.date);
      return {
        ...leave,
        title: `${leave.staff?.fullName} on ${leave.type} (${leave.period === "HD" ? leave.amPm : leave.period})`,
        allDay: true,
        start: date,
        end: date,
        isLessonRecord: false,
      };
    }),
  birthdays: (birthdays) =>
    birthdays.map((birthday) => ({
      ...birthday,
      title: `${birthday.firstName} ${birthday.lastName}'s Birthday`,
      allDay: true,
      start: getBirthday(birthday.dob ?? birthday.birthday),
      end: getBirthday(birthday.dob ?? birthday.birthday),
      clickable: false,
      color: colorMap.Birthday.name,
      isLessonRecord: false,
    })),
};

const defaultFilterParams = {
  centreFilter: {
    all: true,
    HH: false,
    CP: false,
    RB: false,
    TRB: false,
    RBS: false,
    TP: false,
    KLT: false,
    RH: false,
    BH: false,
    ZH: false,
    Home: false,
    SFR: false,
    Online: false,
    "Online-onsite": false,
    "Online-offsite": false,
  },
  teacherFilter: {},
  staffApplicationDisplay: true,
  lessonRecordDisplay: true,
};

const savedFilterParams = getLocalStorage("eventFilters", {});

// Merge the saved filters over the defaults so centres added since the user
// last saved their filters still get a key — without it "Select all" skips
// them, since it only maps over the keys already in centreFilter.
export const initialFilterParams = {
  ...defaultFilterParams,
  ...savedFilterParams,
  centreFilter: {
    ...defaultFilterParams.centreFilter,
    ...savedFilterParams.centreFilter,
  },
};

export const conditionCheckerFactory = ({ teacherFilter, centreFilter }) => [
  (event) =>
    centreFilter[event.centre] && teacherFilter.includes(event?.teacher?.id),
];

export const centres = [
  "KLT",
  "TRB",
  "CP",
  "RB",
  "RBS",
  "TP",
  "BH",
  "ZH",
  "Home",
  "SFR",
  "Online-offsite",
  "Online-onsite",
];

export const eventColors = (event, start, end, isSelected) => {
  return {
    className: `event-${event.isCancelled ? "cancelled" : event.centre ? colorMap[event.centre]?.name : (event.color ?? "default")}`,
  };
};

export class CustomToolbar extends Toolbar {
  render() {
    return (
      <div>
        <div className="text-center font-weight-bold text-white">
          {this.props.label}
        </div>
        <div className="d-flex flex-column flex-sm-row align-items-center align-items-sm-baseline justify-content-sm-between my-3">
          <div className="d-flex">
            <div
              onClick={() => this.navigate("PREV")}
              className="border btn d-flex align-items-center rounded border-info px-2 text-info"
              style={{
                cursor: "pointer",
                paddingBottom: 7,
                paddingTop: 7,
                marginLeft: 1.5,
                marginRight: 1.5,
              }}
            >
              <FiChevronLeft style={{ fontSize: 16 }} />
            </div>

            <div
              onClick={() => this.navigate("TODAY")}
              className="border btn d-flex align-items-center rounded border-info px-2 text-info"
              style={{
                cursor: "pointer",
                paddingBottom: 7,
                paddingTop: 7,
                marginLeft: 1.5,
                marginRight: 1.5,
              }}
            >
              Today
            </div>
            <div
              onClick={() => this.navigate("NEXT")}
              className="border btn d-flex align-items-center rounded border-info px-2 text-info"
              style={{
                cursor: "pointer",
                paddingBottom: 7,
                paddingTop: 7,
                marginLeft: 1.5,
                marginRight: 1.5,
              }}
            >
              <FiChevronRight style={{ fontSize: 16 }} />
            </div>
          </div>
          <div className="d-flex">
            <div
              onClick={this.view.bind(null, "month")}
              className="border btn d-flex align-items-center rounded border-info px-2 text-info"
              style={{
                cursor: "pointer",
                paddingBottom: 7,
                paddingTop: 7,
                marginLeft: 1.5,
                marginRight: 1.5,
              }}
            >
              Month
            </div>
            <div
              onClick={this.view.bind(null, "week")}
              className="border btn d-flex align-items-center rounded border-info px-2 text-info"
              style={{
                cursor: "pointer",
                paddingBottom: 7,
                paddingTop: 7,
                marginLeft: 1.5,
                marginRight: 1.5,
              }}
            >
              Week
            </div>
            <div
              onClick={this.view.bind(null, "day")}
              className="border btn d-flex align-items-center rounded border-info px-2 text-info"
              style={{
                cursor: "pointer",
                paddingBottom: 7,
                paddingTop: 7,
                marginLeft: 1.5,
                marginRight: 1.5,
              }}
            >
              Day
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export const groupStyles = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};
export const groupBadgeStyles = {
  backgroundColor: "#EBECF0",
  borderRadius: "2em",
  color: "#172B4D",
  display: "inline-block",
  fontSize: 12,
  fontWeight: "normal",
  lineHeight: "1",
  minWidth: 1,
  padding: "0.16666666666667em 0.5em",
  textAlign: "center",
};

export const formatGroupLabel = (data) => (
  <div style={groupStyles}>
    <span>{data.label}</span>
    <span style={groupBadgeStyles}>{data.options.length}</span>
  </div>
);
